// The camera over the pixel world. Scale is in DEVICE pixels per art pixel,
// so at rest it is an integer and every art pixel is a square block of
// device pixels. Moves between rooms are cuts behind the lift doors (lift.ts,
// spec section 4); moves inside one room pan.
import type { FloorId } from './assets';
import { liftTimeline, type LiftTrip } from './lift';

export type Rect = [number, number, number, number];

export interface CamState {
  floor: FloorId;
  cx: number;
  cy: number;
  scale: number;
  /** Lit rect; everything outside is dimmed by `dim` (0..1). */
  lit: Rect | null;
  dim: number;
  /** Full-screen ink veil, 0..1 (the reduced-motion crossfade). */
  ink: number;
}

export interface Viewport { w: number; h: number; dpr: number }

/** Room screens frame this much of the world on a desktop. */
export const ROOM_FOCUS = { w: 480, h: 270 };
/** Phones frame a narrow window (spec 7.1). */
export const PHONE_FOCUS_W = 195;
export const DIM_ALPHA = 0.68;

export const MOVE = { pan: 400, reduced: 160 };

/** Largest integer device scale that fits the focus window, at least 1. */
export function fitScale(focusW: number, focusH: number, vp: Viewport): number {
  const s = Math.min((vp.w * vp.dpr) / focusW, (vp.h * vp.dpr) / focusH);
  return Math.max(1, Math.floor(s + 1e-6));
}

/** Keep the view inside the floor image where it can; centre it when the view is larger. */
export function clampCenter(cx: number, cy: number, scale: number, vp: Viewport, fw: number, fh: number): [number, number] {
  const hw = (vp.w * vp.dpr) / scale / 2;
  const hh = (vp.h * vp.dpr) / scale / 2;
  const x = hw * 2 >= fw ? fw / 2 : Math.min(Math.max(cx, hw), fw - hw);
  const y = hh * 2 >= fh ? fh / 2 : Math.min(Math.max(cy, hh), fh - hh);
  return [x, y];
}

/** World point to CSS pixels in the viewport. */
export function worldToScreen(cam: CamState, x: number, y: number, vp: Viewport): [number, number] {
  return [
    ((x - cam.cx) * cam.scale + (vp.w * vp.dpr) / 2) / vp.dpr,
    ((y - cam.cy) * cam.scale + (vp.h * vp.dpr) / 2) / vp.dpr,
  ];
}

export function screenToWorld(cam: CamState, sx: number, sy: number, vp: Viewport): [number, number] {
  return [
    (sx * vp.dpr - (vp.w * vp.dpr) / 2) / cam.scale + cam.cx,
    (sy * vp.dpr - (vp.h * vp.dpr) / 2) / cam.scale + cam.cy,
  ];
}

/** The top-left of the view in device pixels, snapped so an integer scale lands on whole device pixels. */
export function viewOrigin(cam: CamState, vp: Viewport): [number, number] {
  const tx = (vp.w * vp.dpr) / 2 - cam.cx * cam.scale;
  const ty = (vp.h * vp.dpr) / 2 - cam.cy * cam.scale;
  return Number.isInteger(cam.scale) ? [Math.round(tx), Math.round(ty)] : [tx, ty];
}

export function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** Zoom feels even when the scale moves geometrically. */
const lerpScale = (a: number, b: number, t: number) => a * Math.pow(b / a, t);

export interface Shot { floor: FloorId; cx: number; cy: number; scale: number; lit: Rect | null; dim: number }

export interface MovePlan {
  duration: number;
  /** 'lift': a cut between rooms behind the lift doors (lift.ts; the route UI hides for it); 'pan': within one room;
   *  'fade': within one room under reduced motion. */
  kind: 'lift' | 'pan' | 'fade';
  /** The trip the lift draws, for kind 'lift'. */
  lift?: LiftTrip;
  at(ms: number): CamState;
}

const camAt = (s: Shot): CamState => ({ ...s, ink: 0 });

/**
 * Plan a move from one shot to another.
 * - another room: the lift; the camera holds on `from` until the doors are shut, then cuts to `to`;
 * - the same room: a direct pan and scale (a phone following the speaker), or under reduced motion a short fade
 *   through ink.
 */
export function planMove(from: Shot, to: Shot, opts: { sameRoom: boolean; reduced: boolean; trip?: LiftTrip }): MovePlan {
  if (!opts.sameRoom && opts.trip) {
    const trip = opts.trip;
    const tl = liftTimeline(trip);
    return { duration: tl.total, kind: 'lift', lift: trip, at: (ms) => camAt(ms < tl.swap ? from : to) };
  }
  if (opts.reduced) {
    const d = MOVE.reduced;
    return {
      duration: d,
      kind: 'fade',
      at: (ms) => {
        const t = Math.min(1, ms / d);
        return { ...camAt(t < 0.5 ? from : to), ink: 1 - Math.abs(2 * t - 1) };
      },
    };
  }
  const d = MOVE.pan;
  return {
    duration: d,
    kind: 'pan',
    at: (ms) => {
      const t = easeInOut(Math.min(1, ms / d));
      return {
        floor: to.floor, ink: 0, lit: to.lit,
        cx: lerp(from.cx, to.cx, t), cy: lerp(from.cy, to.cy, t), scale: lerpScale(from.scale, to.scale, t),
        dim: lerp(from.dim, to.dim, t),
      };
    },
  };
}
